import { tpoPartTestId } from '../../config/testIds';
import type { MetricFormat } from '../../config/metrics';
import { formatMetricValue } from '../../utils/formatMetricValue';
import { formatPromoScalar, isCellValue } from './utils/promoCellValue';
import type { KpiViewType, MetricType, PromoMetricCell } from './types';

/**
 * A `percent` comparison renders a RATIO, not a value in the KPI's own unit, so
 * the change segment drops the metric's adornments and takes a plain `%`. 2dp
 * matches the backend's `round(((current - previous) / previous) * 100, 2)`.
 */
const PERCENT_CHANGE_FORMAT: MetricFormat = { allowedDecimals: 2, preUnit: '', postUnit: '%' };

/**
 * `'--'` (not `formatMetricValue`'s `'-'`) is the MetricPage empty sentinel,
 * shared with `promoCellValue.EMPTY_DISPLAY`.
 */
export const formatNumber = (value: number | null | undefined, format: MetricFormat): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '--';
    return formatMetricValue(value, format);
};

export const renderNumberCell = (value: number | null | undefined, format: MetricFormat, testId?: string) => (
    <span data-testid={testId ? tpoPartTestId(testId, 'value') : undefined}>{formatNumber(value, format)}</span>
);

interface PromoCellProps {
    value: PromoMetricCell;
    kpiViewType: KpiViewType;
    metricType: MetricType;
    /** The owning KPI's display format, from `resolveKpiFormat`. */
    format: MetricFormat;
    /** Owning cell's test id; the current/change/previous parts derive from it. */
    testId?: string;
}

const isComparisonView = (kvt: KpiViewType): boolean => kvt === 'actuals_vs_pre_snapshot';

const arrowFor = (change: number | null): string => {
    if (change === null || change === undefined) return '';
    if (change > 0) return '\u2191';
    if (change < 0) return '\u2193';
    return '';
};

const directionFor = (change: number | null): 'up' | 'down' | 'flat' | undefined => {
    if (change === null || change === undefined) return undefined;
    if (change > 0) return 'up';
    if (change < 0) return 'down';
    return 'flat';
};

const colourFor = (change: number | null): string => {
    if (change === null || change === undefined || change === 0) return '#52525C';
    return change > 0 ? '#15803D' : '#B91C1C';
};

export const PromoCell = ({ value, kpiViewType, metricType, format, testId }: PromoCellProps) => {
    const currentTestId = testId ? tpoPartTestId(testId, 'current') : undefined;
    if (!isCellValue(value)) return <span data-testid={currentTestId}>--</span>;
    if (!isComparisonView(kpiViewType)) {
        return <span data-testid={currentTestId}>{formatNumber(value.current, format)}</span>;
    }

    // The arrow already carries the direction, so the change reads as a magnitude.
    const arrow = arrowFor(value.change);
    const changeFormat = metricType === 'percent' ? PERCENT_CHANGE_FORMAT : format;
    return (
        <div className="flex items-center justify-end gap-x-3">
            <span className="text-[#18181B] font-medium" data-testid={currentTestId}>
                {formatNumber(value.current, format)}
            </span>
            <span
                style={{ color: colourFor(value.change) }}
                className="font-medium inline-flex items-center gap-x-1"
                data-testid={testId ? tpoPartTestId(testId, 'change') : undefined}
                data-state-direction={directionFor(value.change)}
            >
                {value.change === null ? '--' : formatNumber(Math.abs(value.change), changeFormat)}
                {arrow ? <span>{arrow}</span> : null}
            </span>
            <span className="text-[#A1A1AA]" data-testid={testId ? tpoPartTestId(testId, 'previous') : undefined}>
                {formatNumber(value.previous, format)}
            </span>
        </div>
    );
};

PromoCell.displayName = 'PromoCell';

interface PromoScalarCellProps {
    value: PromoMetricCell;
    testId?: string;
}

export const PromoScalarCell = ({ value, testId }: PromoScalarCellProps) => (
    <span data-testid={testId ? tpoPartTestId(testId, 'current') : undefined}>{formatPromoScalar(value)}</span>
);

PromoScalarCell.displayName = 'PromoScalarCell';
